import { useCallback, useEffect, useState } from 'react';
import * as orgApi from '../../api/orgApi';
import { useAuth } from '../../auth/AuthProvider';

export function OrgMembersPage() {
  const { session } = useAuth();
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = session?.membershipRole === 'admin' || session?.isPlatformAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await orgApi.listMembers();
      setMembers(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <p className="text-sm text-eorig-muted">
        Organization admin role is required to manage members.
      </p>
    );
  }

  async function handleRole(membershipId, role) {
    setError(null);
    try {
      await orgApi.updateMember(membershipId, { role });
      await load();
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  }

  async function handleDisable(membershipId) {
    setError(null);
    try {
      await orgApi.updateMember(membershipId, { status: 'DISABLED' });
      await load();
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  }

  return (
    <div className="space-y-4">
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <div className="overflow-hidden rounded-2xl border border-eorig-border bg-white">
        {loading ? (
          <p className="p-4 text-sm text-eorig-muted">Loading…</p>
        ) : members.length === 0 ? (
          <p className="p-4 text-sm text-eorig-muted">No members.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-eorig-border bg-eorig-surface text-xs uppercase text-eorig-muted">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.membership_id} className="border-b border-eorig-border/70">
                  <td className="px-4 py-3">
                    <div className="font-medium">{member.full_name || '—'}</div>
                    <div className="text-xs text-eorig-muted">{member.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={member.role}
                      onChange={(e) => handleRole(member.membership_id, e.target.value)}
                      className="rounded-lg border border-eorig-border px-2 py-1 text-xs"
                    >
                      <option value="admin">admin</option>
                      <option value="member">member</option>
                      <option value="viewer">viewer</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">{member.status}</td>
                  <td className="px-4 py-3">
                    {member.status === 'ACTIVE' ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-600"
                        onClick={() => handleDisable(member.membership_id)}
                      >
                        Disable
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
